/** 后端 API 封装。 */

const BASE = '/api';

export type HealthResp = {
  ok: true; service: string; version: string; surveys: number; seeded: number;
  seedErrors: number; surveysInvalid: number; ts: number;
};

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  let body: unknown;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const err = new Error(`${path} -> ${res.status}`) as Error & { status: number; body: any };
    err.status = res.status; err.body = body; throw err;
  }
  return body as T;
}

export function checkHealth(): Promise<HealthResp> { return jsonFetch<HealthResp>('/health'); }
export function listSurveys(): Promise<{ items: any[]; errors: any[] }> { return jsonFetch('/surveys'); }
export function getSurvey(id: string): Promise<any> { return jsonFetch(`/surveys/${encodeURIComponent(id)}`); }
export function createSurvey(config: any): Promise<any> { return jsonFetch('/surveys', { method: 'POST', body: JSON.stringify(config) }); }
export function updateSurvey(id: string, config: any): Promise<any> { return jsonFetch(`/surveys/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(config) }); }
export function deleteSurvey(id: string): Promise<any> { return jsonFetch(`/surveys/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
export function validateConfig(config: any): Promise<any> {
  return jsonFetch('/surveys/validate', { method: 'POST', body: JSON.stringify(config) }).catch((e) => {
    if (e?.status === 400 && e?.body) return e.body;
    throw e;
  });
}
export function getQuota(id: string): Promise<any> { return jsonFetch(`/surveys/${encodeURIComponent(id)}/quota`); }

/** 设备标识：localStorage 持久化，用于「每人限填一次」去重（清浏览器数据后会重新生成） */
export function getDeviceId(): string {
  try {
    const KEY = 'wj_device_id';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).replaceAll('-', '');
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

export function submitSurvey(id: string, body: { answers: any; signature?: string }, opts?: { inviteToken?: string }): Promise<any> {
  const headers: Record<string, string> = { 'x-device-id': getDeviceId() };
  if (opts?.inviteToken) headers['x-invite-token'] = opts.inviteToken;
  return jsonFetch(`/surveys/${encodeURIComponent(id)}/submit`, { method: 'POST', body: JSON.stringify(body), headers }).catch((e) => {
    if ((e?.status === 409 || e?.status === 403 || e?.status === 400) && e?.body) return e.body;
    throw e;
  });
}
export function getSubmissionByToken(token: string): Promise<any> { return jsonFetch(`/submissions/${encodeURIComponent(token)}`); }
export function deleteSubmissionByToken(token: string): Promise<any> { return jsonFetch(`/submissions/${encodeURIComponent(token)}`, { method: 'DELETE' }); }
/** 改答：原地更新提交（保留 edit_token，飞书侧收到 event=update 而非重复新行） */
export function updateSubmissionByToken(token: string, body: { answers: any; signature?: string | null }): Promise<any> {
  return jsonFetch(`/submissions/${encodeURIComponent(token)}`, { method: 'PUT', body: JSON.stringify(body) }).catch((e) => {
    if ((e?.status === 409 || e?.status === 403 || e?.status === 400) && e?.body) return e.body;
    throw e;
  });
}
/** 测试连接：带上设置抽屉的草稿配置，未保存也能测（后端草稿优先、回落已保存值） */
export function testFeishuWebhook(surveyId: string, draft?: { webhookUrl?: string; bearerToken?: string }): Promise<any> { return jsonFetch(`/surveys/${encodeURIComponent(surveyId)}/feishu-test`, { method: 'POST', body: JSON.stringify(draft ?? {}) }).catch((e) => e?.body ?? Promise.reject(e)); }

export type AdminSession = { ok: boolean; configured: boolean };
export type SubmissionRecord = { id: number; surveyId: string; answers: Record<string, unknown>; signature: string | null; editToken: string; score: number | null; createdAt: number; updatedAt: number };
export type SubmissionList = { items: SubmissionRecord[]; total: number; limit: number; offset: number };

export function getAdminSession(): Promise<AdminSession> { return jsonFetch('/admin/session'); }
export function adminLogin(password: string): Promise<{ ok: true }> { return jsonFetch('/admin/login', { method: 'POST', body: JSON.stringify({ password }) }); }
export function adminLogout(): Promise<{ ok: true }> { return jsonFetch('/admin/logout', { method: 'POST', body: '{}' }); }
export function listAdminSubmissions(params: Record<string, string | number | undefined> = {}): Promise<SubmissionList> {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => { if (value !== undefined && value !== '') query.set(key, String(value)); });
  return jsonFetch(`/admin/submissions?${query}`);
}
export function getAdminSubmissionStats(): Promise<{ total: number; today: number; latestAt: number | null }> { return jsonFetch('/admin/submissions/stats'); }
export function getAdminSubmission(id: number): Promise<{ submission: SubmissionRecord }> { return jsonFetch(`/admin/submissions/${id}`); }
/** 后台删除单条提交（释放配额，连附件/签署文档一起清理） */
export function deleteAdminSubmission(id: number): Promise<{ ok: true }> { return jsonFetch(`/admin/submissions/${id}`, { method: 'DELETE' }); }
/** 清空某问卷全部提交数据（问卷保留，可继续收集） */
export function purgeSurveySubmissions(surveyId: string): Promise<{ ok: true; deleted: number }> { return jsonFetch(`/admin/surveys/${encodeURIComponent(surveyId)}/submissions`, { method: 'DELETE' }); }
/** 某问卷总分统计（评分题） */
export function getScoreStats(surveyId: string): Promise<{ total: number; scored: number; avg: number | null; min: number | null; max: number | null }> { return jsonFetch(`/admin/surveys/${encodeURIComponent(surveyId)}/score-stats`); }
/** Word(.docx) → AI 解析成问卷草稿（进编辑器人工审核后保存） */
export function importDocAsSurvey(dataUrl: string): Promise<{ ok: true; config: any; warnings: string[] }> {
  return jsonFetch('/admin/surveys/import-doc', { method: 'POST', body: JSON.stringify({ dataUrl }) });
}
/** 一键创建关联多维表格（直连飞书；已创建则返回现有链接） */
export function createFeishuTable(surveyId: string): Promise<{ ok: true; url: string; existing?: boolean }> {
  return jsonFetch(`/surveys/${encodeURIComponent(surveyId)}/feishu-table`, { method: 'POST', body: '{}' });
}
